import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import CustomDateField from "../CustomDateField";
import CustomLabel from "../CustomLabel";
import { COLORS } from "../../assets/theme";
import { getCoachPayout } from "../../services/CoachesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { Coach, CoachPayout } from "./types";
import {
  COMPENSATION_OPTIONS,
  formatMoney,
  formatTime,
  isCustomPerClassCompensation,
  isPayoutAmountSet,
  labelForValue,
} from "./classUtils";
import CoachPayoutSessionsDialog from "./CoachPayoutSessionsDialog";
import CoachPayDialog from "./CoachPayDialog";

interface CoachPayoutDialogProps {
  open: boolean;
  coach: Coach | null;
  onClose: () => void;
  /** Fired after a payment is recorded, so a caller showing the coaches list can refresh total_payout. */
  onPaid?: () => void;
}

const toInputDate = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const defaultRange = () => {
  const now = new Date();
  return {
    from: toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toInputDate(now),
  };
};

const SummaryTile: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      flex: "1 1 140px",
      border: "1px solid #eee",
      borderRadius: 2,
      p: 1.5,
      bgcolor: COLORS.MainBackground,
    }}
  >
    <Typography sx={{ fontSize: 12, color: "#666" }}>{label}</Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{value}</Typography>
  </Box>
);

/**
 * Read-only computed report. Only sessions marked DONE are counted, so an empty
 * result usually means the sessions in range have not been completed yet.
 */
const CoachPayoutDialog: React.FC<CoachPayoutDialogProps> = ({
  open,
  coach,
  onClose,
  onPaid,
}) => {
  const [range, setRange] = React.useState(defaultRange);
  const [payout, setPayout] = React.useState<CoachPayout | null>(null);
  const [mode, setMode] = React.useState<"outstanding" | "period">("outstanding");
  const [loading, setLoading] = React.useState(false);
  const [amountsDialogOpen, setAmountsDialogOpen] = React.useState(false);
  const [payDialogOpen, setPayDialogOpen] = React.useState(false);

  const coachId = coach?.id ? String(coach.id) : "";
  const isCustomPerClass = isCustomPerClassCompensation(
    payout?.compensation_type ?? coach?.coach_profile?.compensation_type
  );

  const fetchPayout = React.useCallback(
    async (from?: string, to?: string) => {
      if (!coachId) return;
      setLoading(true);
      try {
        const result = await getCoachPayout(
          coachId,
          from && to ? { from, to } : undefined
        );
        setPayout(result);
      } catch (error) {
        toast.error(getErrorMessage(error, "Failed to load the payout report."));
        setPayout(null);
      } finally {
        setLoading(false);
      }
    },
    [coachId]
  );

  const refetchCurrent = React.useCallback(() => {
    if (mode === "period") {
      fetchPayout(range.from, range.to);
    } else {
      fetchPayout();
    }
  }, [mode, range, fetchPayout]);

  React.useEffect(() => {
    if (!open || !coachId) return;
    setRange(defaultRange());
    setMode("outstanding");
    fetchPayout();
  }, [open, coachId, fetchPayout]);

  const sessions = payout?.sessions || [];
  // Derived from the rows themselves so the banner can never disagree with
  // what the table below actually shows.
  const pendingAmountCount = isCustomPerClass
    ? sessions.filter((s) => !isPayoutAmountSet(s)).length
    : 0;
  const isOutstandingView = mode === "outstanding";
  const outstandingAmount = Number(payout?.total_payout) || 0;

  return (
    <>
      <CustomDialog
        open={open}
        onClose={onClose}
        paperSx={{ minWidth: { sm: 720 }, maxWidth: 900 }}
        title={`Payout — ${coach?.full_name || "Coach"}`}
        actions={
          <>
            {isCustomPerClass && (
              <CustomButton
                label="Enter / Edit Amounts"
                variant="outlined"
                onClick={() => setAmountsDialogOpen(true)}
                sx={{ mr: "auto" }}
              />
            )}
            {isOutstandingView && outstandingAmount > 0 && (
              <CustomButton
                label="Pay"
                variant="contained"
                bgColor={COLORS.MainColor}
                textColor={COLORS.WhiteText}
                onClick={() => setPayDialogOpen(true)}
              />
            )}
            <CustomButton label="Close" variant="outlined" onClick={onClose} />
          </>
        }
      >
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Box sx={{ flex: "1 1 180px" }}>
            <CustomLabel text="From" fontSize={14} fontWeight={500} />
            <CustomDateField
              value={range.from}
              onChange={(v: string) => setRange((prev) => ({ ...prev, from: v }))}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 180px" }}>
            <CustomLabel text="To" fontSize={14} fontWeight={500} />
            <CustomDateField
              value={range.to}
              onChange={(v: string) => setRange((prev) => ({ ...prev, to: v }))}
              fullWidth
            />
          </Box>
          <CustomButton
            label="Apply"
            variant="contained"
            bgColor={COLORS.MainColor}
            textColor={COLORS.WhiteText}
            disabled={loading}
            onClick={() => {
              if (!range.from || !range.to) {
                toast.error("Select both a from and to date.");
                return;
              }
              setMode("period");
              fetchPayout(range.from, range.to);
            }}
            sx={{ mb: 0.5 }}
          />
          {!isOutstandingView && (
            <CustomButton
              label="View Outstanding Balance"
              variant="outlined"
              disabled={loading}
              onClick={() => {
                setRange(defaultRange());
                setMode("outstanding");
                fetchPayout();
              }}
              sx={{ mb: 0.5 }}
            />
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2 }}>
              <SummaryTile
                label="Compensation"
                value={labelForValue(
                  COMPENSATION_OPTIONS.map((o) => ({
                    label: o.label,
                    value: o.value,
                  })),
                  payout?.compensation_type
                )}
              />
              {!isCustomPerClass && (
                <SummaryTile
                  label="Rate"
                  value={formatMoney(payout?.compensation_rate)}
                />
              )}
              <SummaryTile
                label="Sessions done"
                value={String(payout?.session_count ?? 0)}
              />
              <SummaryTile
                label={isOutstandingView ? "Outstanding balance" : "Total payout"}
                value={formatMoney(payout?.total_payout ?? 0)}
              />
              {isOutstandingView && payout?.total_paid !== undefined && (
                <SummaryTile label="Total paid" value={formatMoney(payout.total_paid)} />
              )}
            </Box>

            {isCustomPerClass && pendingAmountCount > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#fbf0dd",
                  border: "1px solid #f0d9a8",
                }}
              >
                <Typography sx={{ fontSize: 13.5, color: "#b9791a", fontWeight: 600 }}>
                  {`${pendingAmountCount} session${
                    pendingAmountCount === 1 ? "" : "s"
                  } still need an amount entered.`}
                </Typography>
                <CustomButton
                  label="Enter Amounts"
                  variant="outlined"
                  onClick={() => setAmountsDialogOpen(true)}
                />
              </Box>
            )}

            <Box sx={{ mt: 3, overflowX: "auto" }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                {isOutstandingView
                  ? "Sessions counted toward the outstanding balance"
                  : "Completed sessions in period"}
              </Typography>
              {sessions.length === 0 ? (
                <Typography sx={{ color: "#777", fontSize: 14 }}>
                  No completed sessions in this period. Sessions only count once they
                  are marked as done.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Class</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      {isCustomPerClass ? (
                        <TableCell align="right">Amount</TableCell>
                      ) : (
                        <TableCell align="right">Hours</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((session) => (
                      <TableRow key={session.appointment_id}>
                        <TableCell>{session.class_name || "-"}</TableCell>
                        <TableCell>{formatTableDate(session.start_at)}</TableCell>
                        <TableCell>
                          {`${formatTime(session.start_at)} - ${formatTime(
                            session.end_at
                          )}`}
                        </TableCell>
                        {isCustomPerClass ? (
                          <TableCell align="right">
                            {isPayoutAmountSet(session) ? (
                              formatMoney(session.amount)
                            ) : (
                              <Chip
                                size="small"
                                label="Pending"
                                sx={{
                                  bgcolor: "#fbf0dd",
                                  color: "#b9791a",
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </TableCell>
                        ) : (
                          <TableCell align="right">
                            {session.duration_hours ?? "-"}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </>
        )}
      </CustomDialog>

      <CoachPayoutSessionsDialog
        open={amountsDialogOpen}
        coachId={coachId || null}
        coachName={coach?.full_name}
        onClose={() => setAmountsDialogOpen(false)}
        onChanged={refetchCurrent}
      />

      <CoachPayDialog
        open={payDialogOpen}
        coach={coach}
        payout={isOutstandingView ? payout : null}
        onClose={() => setPayDialogOpen(false)}
        onPaid={() => {
          refetchCurrent();
          onPaid?.();
        }}
      />
    </>
  );
};

export default CoachPayoutDialog;
